import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { GlAccount, TemplateLine, TemplateLineSide, TemplateLineSource } from '../types';
import { MAX_ROW_LABEL, displayName, findAccount, newAccountProblem, sortedChart } from '../lib/glChart';
import { groupedUses, lineKeyFor, setUsedAs, unusedLines, findUseNamed, usedAs, usedAsOptions, type UsedAsOption } from '../lib/glOneList';
import UsesPicker from './UsesPicker';
import { TEMPLATE_SOURCES } from '../lib/clientTemplate';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  warn: { color: tokens.colorPaletteDarkOrangeForeground1, fontSize: tokens.fontSizeBase200 },
  usedAs: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  // Fluent's Table is fixed-layout (equal columns), so each column gets its share and each control fills its cell:
  // a 220px "used as" in an equal-width cell spilled over Source (the polish walk of deploy #21).
  fill: { width: '100%', minWidth: 0 },
  colCode: { width: '16%' },
  colName: { width: '27%' },
  colUses: { width: '31%' },
  colSource: { width: '10%' },
  colActions: { width: '16%' },
  codeCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  inline: { display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'end', flexWrap: 'wrap' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  add: { display: 'grid', gridTemplateColumns: '1fr 2fr 2fr auto', gap: tokens.spacingHorizontalS, alignItems: 'end' },
  unused: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, alignItems: 'center' },
});

export interface GlListValue {
  accounts: GlAccount[];
  /** Role (template or custom) → the account code it posts to. */
  roles: Record<string, string>;
  rowLabels: Record<string, string>;
}

export interface GlChartSectionProps {
  value: GlListValue;
  onChange: (next: GlListValue) => void;
  /** How many journals name each code (§B6). Undefined while loading: nothing is offered for deletion until it is known. */
  usage?: Readonly<Record<string, number>>;
  /** True when the screen has unsaved edits: a code change refreshes the screen, so it waits for Save (§B3). */
  dirty?: boolean;
  /** Change a code now (§B3). Resolves to how many journals followed. */
  onChangeCode?: (from: string, to: string) => Promise<number>;
  /** A new custom line the bookkeeper asked to add to the journal template too (§A8). */
  onAddTemplateLine?: (line: TemplateLine) => void;
  /** The client is still on the default template, so adding a line starts its own (said beside the tick). */
  onDefaultTemplate?: boolean;
}

/**
 * G/L Mappings as ONE list (ruled by Christian, 2026-10-28): the client's chart, each account with a USED AS — a
 * template role, a custom line, or nothing. 🗳 **And the code is the key (ruled 2026-10-29, docs/client-journal-template.md
 * §B):** no type on screen (it stays in the record for the Xero pull); codes are unique, and CHANGED with their own action
 * so every journal follows; the list is in Xero's order, code then name; archived accounts have their own tab with
 * Restore; and an account is DELETED only when no journal has ever used its code.
 */
export default function GlChartSection({
  value, onChange, usage, dirty = false, onChangeCode, onAddTemplateLine, onDefaultTemplate = false,
}: GlChartSectionProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts, roles, rowLabels } = value;

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [newUses, setNewUses] = useState<string[]>([]);
  const [tried, setTried] = useState(false);
  // Which row is naming a new custom line ('' = the add row), and the label so far.
  const [namingFor, setNamingFor] = useState<string | null>(null);
  const [lineLabel, setLineLabel] = useState('');
  const [lineTried, setLineTried] = useState(false);
  const [toTemplate, setToTemplate] = useState(true);
  const [lineSide, setLineSide] = useState<TemplateLineSide>('credit');
  const [lineSource, setLineSource] = useState<TemplateLineSource>('pos');
  const [movedNote, setMovedNote] = useState<string | null>(null);
  // The code change in progress: which account, the new code, and how it went.
  const [changing, setChanging] = useState<{ from: string; to: string; tried: boolean; busy: boolean; error: string | null } | null>(null);
  const [changedNote, setChangedNote] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const options = usedAsOptions(roles, rowLabels);
  const labelOf = (o: UsedAsOption) => (o.custom ? o.label ?? o.key : t(`glMappingPage.fields.${o.key}`, { defaultValue: o.key }));
  const labelOfKey = (key: string) => labelOf(options.find((o) => o.key === key) ?? { key, custom: true, label: rowLabels[key] ?? key });
  const problem = newAccountProblem(accounts, code);
  const lineOk = lineLabel.trim().length > 0 && lineLabel.trim().length <= MAX_ROW_LABEL;

  const sorted = sortedChart(accounts);
  const active = sorted.filter((a) => a.status !== 'ARCHIVED');
  const archived = sorted.filter((a) => a.status === 'ARCHIVED');
  const sameCode = (a: GlAccount, c: string) => a.code.trim().toLowerCase() === c.trim().toLowerCase();

  function updateAccount(accountCode: string, patch: Partial<GlAccount>) {
    onChange({ ...value, accounts: accounts.map((a) => (sameCode(a, accountCode) ? { ...a, ...patch } : a)) });
  }

  /**
   * Set what an account is used as. One step (wave 4 §2): a typed account with no name yet takes the name of the use just
   * picked; it stays editable.
   */
  function chooseUses(accountCode: string, chosen: string[], current: GlAccount[] = accounts) {
    const result = setUsedAs(roles, accountCode, chosen);
    setMovedNote(
      result.moved.length
        ? result.moved.map((m) => t('glMappingPage.oneList.moved', { use: labelOfKey(m.role), from: m.from, to: accountCode })).join(' ')
        : null,
    );
    const picked = chosen.find((k) => !usedAs(roles, accountCode).includes(k));
    const named = current.map((a) =>
      sameCode(a, accountCode) && a.source === 'typed' && !(a.name ?? '').trim() && picked ? { ...a, name: labelOfKey(picked) } : a,
    );
    onChange({ ...value, accounts: named, roles: result.roles });
  }

  /** "New custom line…", under a row's list: the label box for that row. */
  function startNaming(rowKey: string) {
    setNamingFor(rowKey);
    setLineLabel('');
    setLineTried(false);
    setToTemplate(true);
    setLineSide('credit');
    setLineSource('pos');
  }

  /**
   * A typed account's name, as typed. One step (wave 4 §2): a name that is exactly a known use's, on an account with no use
   * yet, picks that use too. Either stays editable.
   */
  function renameAccount(accountCode: string, typed: string) {
    const next = accounts.map((a) => (sameCode(a, accountCode) ? { ...a, name: typed } : a));
    const match = usedAs(roles, accountCode).length === 0 ? findUseNamed(typed, options, labelOf) : null;
    if (!match) {
      onChange({ ...value, accounts: next });
      return;
    }
    chooseUses(accountCode, [match], next);
  }

  /** The add row's name, as typed: the same one step, onto the add row's uses. */
  function nameNew(typed: string) {
    setName(typed);
    const match = newUses.length === 0 ? findUseNamed(typed, options, labelOf) : null;
    if (match) setNewUses([match]);
  }

  /** The add row's uses: a blank name takes the name of the use just picked (§2). */
  function chooseNewUses(chosen: string[]) {
    const picked = chosen.find((k) => !newUses.includes(k));
    if (picked && !name.trim()) setName(labelOfKey(picked));
    setNewUses(chosen);
  }

  function addLine(rowKey: string, current: string[]) {
    setLineTried(true);
    if (!lineOk) return;
    // One key per name (wave 4 §1.2.1): a name the client already has is that line, never a second key.
    const { key, existing } = lineKeyFor(lineLabel, rowLabels, [...Object.keys(roles), ...Object.keys(rowLabels)]);
    const labels = existing ? rowLabels : { ...rowLabels, [key]: lineLabel.trim() };
    if (toTemplate && onAddTemplateLine) onAddTemplateLine({ key, label: labels[key], side: lineSide, source: lineSource });
    setNamingFor(null);
    setLineLabel('');
    setLineTried(false);
    // One step (§2): an account with no name yet takes the line's name. Said with the label itself: the new line is not
    // in this render's options yet, so its key has no label to look up.
    if (rowKey === '') {
      onChange({ ...value, rowLabels: labels });
      if (!name.trim()) setName(labels[key]);
      setNewUses([...current.filter((k) => k !== key), key]);
    } else {
      const result = setUsedAs(roles, rowKey, [...current.filter((k) => k !== key), key]);
      const named = accounts.map((a) =>
        sameCode(a, rowKey) && a.source === 'typed' && !(a.name ?? '').trim() ? { ...a, name: labels[key] } : a,
      );
      onChange({ ...value, accounts: named, rowLabels: labels, roles: result.roles });
    }
  }

  function removeLine(key: string) {
    const { [key]: _label, ...labels } = rowLabels;
    const { [key]: _code, ...rest } = roles;
    onChange({ ...value, rowLabels: labels, roles: rest });
  }

  function addAccount() {
    setTried(true);
    if (problem) return;
    const added: GlAccount = { code: code.trim(), name: name.trim() || null, status: 'ACTIVE', source: 'typed' };
    const result = setUsedAs(roles, added.code, newUses);
    onChange({ ...value, accounts: [...accounts, added], roles: result.roles });
    setMovedNote(
      result.moved.length
        ? result.moved.map((m) => t('glMappingPage.oneList.moved', { use: labelOfKey(m.role), from: m.from, to: added.code })).join(' ')
        : null,
    );
    setCode('');
    setName('');
    setNewUses([]);
    setTried(false);
  }

  /** Deleting drops the account and whatever it was used as; the confirmation said which. Saved with Save. */
  function deleteAccount(accountCode: string) {
    const result = setUsedAs(roles, accountCode, []);
    onChange({ ...value, accounts: accounts.filter((a) => !sameCode(a, accountCode)), roles: result.roles });
    setConfirmDelete(null);
  }

  const changeProblem = (from: string, to: string): 'codeRequired' | 'duplicate' | 'unchanged' | null => {
    if (!to.trim()) return 'codeRequired';
    if (to.trim() === from) return 'unchanged';
    const other = findAccount(accounts, to);
    return other && !sameCode(other, from) ? 'duplicate' : null;
  };

  async function changeCode() {
    if (!changing || !onChangeCode) return;
    const issue = changeProblem(changing.from, changing.to);
    if (issue) {
      setChanging({ ...changing, tried: true });
      return;
    }
    setChanging({ ...changing, tried: true, busy: true, error: null });
    try {
      const moved = await onChangeCode(changing.from, changing.to.trim());
      setChangedNote(t('glMappingPage.code.changed', { from: changing.from, to: changing.to.trim(), count: moved }));
      setChanging(null);
    } catch (e) {
      setChanging({ ...changing, tried: true, busy: false, error: e instanceof Error ? e.message : t('common.unknownError') });
    }
  }

  /**
   * The "used as" control for one row (an account, or the add row with key ''). A render FUNCTION, not a component
   * defined in here: a nested component would be a new type every render, remounting the Dropdown (closing it after
   * each pick) and the label box (losing focus on each keystroke).
   */
  function usesControl({ rowKey, chosen, apply, label }: { rowKey: string; chosen: string[]; apply: (c: string[]) => void; label: string }) {
    return (
      <div className={styles.usedAs}>
        <UsesPicker
          label={label}
          placeholder={t('glMappingPage.oneList.notUsed')}
          selected={chosen}
          onChange={apply}
          options={groupedUses(roles, rowLabels, rowKey).map((o) => ({
            value: o.key,
            label: labelOf(o),
            group: t(`glMappingPage.oneList.groups.${o.group}`),
            dimmed: o.group === 'elsewhere',
            note: o.elsewhere ? t('glMappingPage.oneList.nowOn', { code: o.elsewhere }) : undefined,
          }))}
          action={{ label: t('glMappingPage.oneList.newLine'), onClick: () => startNaming(rowKey) }}
        />
        {namingFor === rowKey && (
          <div className={styles.usedAs}>
            <div className={styles.inline}>
              <Field
                label={t('glMappingPage.addRowLabel')}
                validationState={lineTried && !lineOk ? 'error' : 'none'}
                validationMessage={lineTried && !lineOk ? t('glMappingPage.addRowProblem', { max: MAX_ROW_LABEL }) : undefined}
              >
                <Input size="small" value={lineLabel} onChange={(_e, data) => setLineLabel(data.value)} />
              </Field>
              <Button size="small" onClick={() => addLine(rowKey, chosen)}>{t('glMappingPage.addRowButton')}</Button>
            </div>
            {onAddTemplateLine && (
              <>
                <Checkbox
                  checked={toTemplate}
                  onChange={(_e, data) => setToTemplate(data.checked === true)}
                  label={t(onDefaultTemplate ? 'glMappingPage.template.addToTemplateStarts' : 'glMappingPage.template.addToTemplate')}
                />
                {toTemplate && (
                  <div className={styles.inline}>
                    <Field label={t('glMappingPage.template.columns.side')}>
                      <Select size="small" value={lineSide} onChange={(_e, data) => setLineSide(data.value as TemplateLineSide)}>
                        <option value="credit">{t('glMappingPage.template.sides.credit')}</option>
                        <option value="debit">{t('glMappingPage.template.sides.debit')}</option>
                      </Select>
                    </Field>
                    <Field label={t('glMappingPage.template.columns.source')}>
                      <Select size="small" value={lineSource} onChange={(_e, data) => setLineSource(data.value as TemplateLineSource)}>
                        {TEMPLATE_SOURCES.map((s) => <option key={s} value={s}>{t(`glMappingPage.template.sources.${s}`)}</option>)}
                      </Select>
                    </Field>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  /** The code cell: the code, and the code change in place when it is this row's. */
  function codeCell(a: GlAccount) {
    if (changing?.from !== a.code) return a.code;
    const issue = changing.tried ? changeProblem(changing.from, changing.to) : null;
    const used = usage?.[a.code] ?? 0;
    return (
      <div className={styles.codeCell}>
        <Field
          label={t('glMappingPage.code.newCode', { code: a.code })}
          validationState={issue || changing.error ? 'error' : 'none'}
          validationMessage={
            changing.error ?? (issue ? t(`glMappingPage.code.problems.${issue}`, { code: changing.to.trim() }) : undefined)
          }
        >
          <Input size="small" value={changing.to} onChange={(_e, data) => setChanging({ ...changing, to: data.value, error: null })} />
        </Field>
        <Text className={styles.muted}>{t('glMappingPage.code.follows', { count: used })}</Text>
        <div className={styles.actions}>
          <Button size="small" appearance="primary" disabled={changing.busy} onClick={() => void changeCode()}>
            {t('glMappingPage.code.change')}
          </Button>
          <Button size="small" disabled={changing.busy} onClick={() => setChanging(null)}>{t('common.cancel')}</Button>
        </div>
      </div>
    );
  }

  function actionsCell(a: GlAccount, isArchived: boolean) {
    if (a.source !== 'typed') return t(isArchived ? 'glMappingPage.chart.archived' : 'glMappingPage.chart.active');
    const uses = usedAs(roles, a.code);
    const deletable = usage !== undefined && !usage[a.code];
    if (confirmDelete === a.code) {
      return (
        <div className={styles.usedAs}>
          <Text className={styles.warn}>
            {uses.length
              ? t('glMappingPage.chart.deleteConfirmUsed', { code: a.code, uses: uses.map(labelOfKey).join(', ') })
              : t('glMappingPage.chart.deleteConfirm', { code: a.code })}
          </Text>
          <div className={styles.actions}>
            <Button size="small" appearance="primary" onClick={() => deleteAccount(a.code)}>{t('glMappingPage.chart.delete')}</Button>
            <Button size="small" onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.actions}>
        {!isArchived && onChangeCode && changing?.from !== a.code && (
          <Button
            size="small"
            appearance="subtle"
            disabled={dirty}
            title={dirty ? t('glMappingPage.code.saveFirst') : undefined}
            aria-label={t('glMappingPage.code.changeOf', { code: a.code })}
            onClick={() => { setChangedNote(null); setChanging({ from: a.code, to: a.code, tried: false, busy: false, error: null }); }}
          >
            {t('glMappingPage.code.changeButton')}
          </Button>
        )}
        <Button
          size="small"
          aria-label={t(isArchived ? 'glMappingPage.chart.restoreOf' : 'glMappingPage.chart.archiveOf', { code: a.code })}
          onClick={() => updateAccount(a.code, { status: isArchived ? 'ACTIVE' : 'ARCHIVED' })}
        >
          {t(isArchived ? 'glMappingPage.chart.restore' : 'glMappingPage.chart.archive')}
        </Button>
        {deletable && (
          <Button
            size="small"
            appearance="subtle"
            aria-label={t('glMappingPage.chart.deleteOf', { code: a.code })}
            onClick={() => setConfirmDelete(a.code)}
          >
            {t('glMappingPage.chart.delete')}
          </Button>
        )}
      </div>
    );
  }

  function table(rows: GlAccount[], isArchived: boolean) {
    if (rows.length === 0) {
      return <Text className={styles.muted}>{t(isArchived ? 'glMappingPage.chart.noArchived' : 'glMappingPage.chart.empty')}</Text>;
    }
    return (
      <Table size="small" aria-label={t(isArchived ? 'glMappingPage.chart.archivedTab' : 'glMappingPage.chart.heading')}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell className={styles.colCode}>{t('glMappingPage.chart.columns.code')}</TableHeaderCell>
            <TableHeaderCell className={styles.colName}>{t('glMappingPage.chart.columns.name')}</TableHeaderCell>
            <TableHeaderCell className={styles.colUses}>{t('glMappingPage.oneList.usedAs')}</TableHeaderCell>
            <TableHeaderCell className={styles.colSource}>{t('glMappingPage.chart.columns.source')}</TableHeaderCell>
            <TableHeaderCell className={styles.colActions}>{t('glMappingPage.chart.columns.status')}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => {
            const uses = usedAs(roles, a.code);
            return (
              <TableRow key={`${a.source}-${a.xeroAccountId ?? a.code}`}>
                <TableCell>{codeCell(a)}</TableCell>
                <TableCell>
                  {a.source === 'typed' ? (
                    <Input
                      className={styles.fill}
                      size="small"
                      aria-label={t('glMappingPage.chart.nameOf', { code: a.code })}
                      value={a.name ?? ''}
                      onChange={(_e, data) => renameAccount(a.code, data.value)}
                    />
                  ) : (
                    displayName(a)
                  )}
                </TableCell>
                <TableCell>
                  {usesControl({
                    rowKey: a.code,
                    chosen: uses,
                    apply: (c) => chooseUses(a.code, c),
                    label: t('glMappingPage.oneList.usedAsOf', { code: a.code }),
                  })}
                  {isArchived && uses.length > 0 && (
                    <Text className={styles.warn}>
                      {t('glMappingPage.oneList.archivedInUse', { uses: uses.map(labelOfKey).join(', ') })}
                    </Text>
                  )}
                </TableCell>
                <TableCell>{t(a.source === 'xero' ? 'glMappingPage.chart.sourceXero' : 'glMappingPage.chart.sourceTyped')}</TableCell>
                <TableCell>{actionsCell(a, isArchived)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  const unused = unusedLines(roles, rowLabels);

  return (
    <section className={styles.section} aria-labelledby="gl-chart-heading">
      <Text as="h2" id="gl-chart-heading" className={styles.heading}>{t('glMappingPage.chart.heading')}</Text>
      <Text className={styles.muted}>{t('glMappingPage.oneList.intro')}</Text>

      {movedNote && (
        <MessageBar intent="info">
          <MessageBarBody>{movedNote}</MessageBarBody>
        </MessageBar>
      )}
      {changedNote && (
        <MessageBar intent="success">
          <MessageBarBody>{changedNote}</MessageBarBody>
        </MessageBar>
      )}

      <TabList selectedValue={tab} onTabSelect={(_e, data) => setTab(data.value as 'active' | 'archived')}>
        <Tab value="active">{t('glMappingPage.chart.activeTab', { count: active.length })}</Tab>
        <Tab value="archived">{t('glMappingPage.chart.archivedTabCount', { count: archived.length })}</Tab>
      </TabList>

      {tab === 'active' ? table(active, false) : table(archived, true)}

      {tab === 'active' && (
        /* Adding an account is ONE step: its code, name and what it is used as, together. */
        <div className={styles.add}>
          <Field
            label={t('glMappingPage.chart.addCode')}
            validationState={tried && problem ? 'error' : 'none'}
            validationMessage={tried && problem ? t(`glMappingPage.chart.problems.${problem}`, { code: code.trim() }) : undefined}
          >
            <Input value={code} onChange={(_e, data) => setCode(data.value)} />
          </Field>
          <Field label={t('glMappingPage.chart.addName')}>
            <Input value={name} onChange={(_e, data) => nameNew(data.value)} />
          </Field>
          <Field label={t('glMappingPage.oneList.usedAs')}>
            {usesControl({ rowKey: '', chosen: newUses, apply: chooseNewUses, label: t('glMappingPage.oneList.usedAsNew') })}
          </Field>
          <Button onClick={addAccount}>{t('glMappingPage.chart.addButton')}</Button>
        </div>
      )}

      {unused.length > 0 && (
        <div className={styles.unused}>
          <Text className={styles.muted}>{t('glMappingPage.oneList.unusedHeading')}</Text>
          {unused.map((o) => (
            <span key={o.key}>
              <Text>{labelOf(o)}</Text>
              {o.custom && (
                <Button size="small" appearance="subtle" aria-label={t('glMappingPage.removeRowOf', { label: labelOf(o) })} onClick={() => removeLine(o.key)}>
                  {t('glMappingPage.removeRow')}
                </Button>
              )}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
